/**
 * Asset URL Parameter Utilities
 *
 * This file contains utility functions for parsing and managing URL parameters
 * for asset filtering and navigation.
 */

import type React from 'react';

import type { Filters } from '@/hooks/useAssets';

/**
 * Applies URL search parameters to asset filters
 *
 * Extracts attackSurface, source, search, and profile parameters from URL and
 * applies them to the filter state. This allows for deep-linking to filtered
 * asset views.
 *
 * @param searchParams - URLSearchParams object from react-router
 * @param setFilters - Function to update filter state
 * @param clearParams - Function to clear URL parameters after applying
 */
export const applyUrlParamsToFilters = (
  searchParams: URLSearchParams,
  setFilters: React.Dispatch<React.SetStateAction<Filters>>,
  clearParams: () => void
): void => {
  const attackSurfaceParam = searchParams.get('attackSurface');
  const sourceParam = searchParams.get('source');
  const searchParam = searchParams.get('search');
  const profileParam = searchParams.get('profile');

  if (attackSurfaceParam || sourceParam || searchParam || profileParam) {
    setFilters((prev: Filters) => {
      const newFilters: Filters = { ...prev };

      if (attackSurfaceParam) {
        newFilters.advancedFilters = {
          ...newFilters.advancedFilters,
          attackSurface: [attackSurfaceParam],
        };
      }

      if (sourceParam) {
        newFilters.advancedFilters = {
          ...newFilters.advancedFilters,
          source: [sourceParam],
        };
      }

      if (searchParam) {
        newFilters.search = searchParam;
      }

      if (profileParam) {
        newFilters.advancedFilters = {
          ...newFilters.advancedFilters,
          profile: [profileParam],
        };
      }

      return newFilters;
    });

    // Clear URL params after applying
    clearParams();
  }
};

/**
 * Builds a URL to the assets page pre-filtered by scan profile.
 *
 * @param profileId - The scan profile ID to filter by
 * @param pathPrefix - Tenant-prefixed path prefix (everything before /settings)
 */
export function assetsUrlForProfile(
  profileId: string,
  pathPrefix: string
): string {
  return `${pathPrefix}/assets?profile=${encodeURIComponent(profileId)}`;
}
